/**
 * Leave request attachments - medical certificates and the like.
 *
 * Two pieces:
 *   LeaveAttachmentUploader - inside the "Request Leave" dialog. Uploads
 *     immediately and hands the resulting document IDs back to the form, so a
 *     mandatory type is submittable.
 *   LeaveAttachmentList - read-only, for the request details and the approval
 *     dialogs. Only the employee, their approving manager and HR can open them;
 *     the API refuses everyone else.
 *
 * PDF, JPG, PNG. 10 MB per file. Up to three files.
 */
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Eye, FileText, Loader2, Paperclip, Upload, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import DocumentPreview from '@/components/documents/DocumentPreview';
import { downloadWithAuth } from '@/lib/downloadWithAuth';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export const MAX_LEAVE_ATTACHMENTS = 3;
const MAX_BYTES = 10 * 1024 * 1024;
const ACCEPT = '.pdf,.jpg,.jpeg,.png';

const readableSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const errorText = (err, fallback) =>
  err.response?.data?.detail?.error || err.response?.data?.detail || fallback;

export const LeaveAttachmentUploader = ({ token, value = [], onChange, isRTL, required }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const handleFile = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    if (value.length >= MAX_LEAVE_ATTACHMENTS) {
      setError(isRTL ? `يمكن إرفاق ${MAX_LEAVE_ATTACHMENTS} ملفات كحد أقصى` : `At most ${MAX_LEAVE_ATTACHMENTS} files`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setError(isRTL ? 'الحد الأقصى لحجم الملف 10 ميجابايت' : 'Maximum file size is 10 MB');
      return;
    }

    const body = new FormData();
    body.append('file', file);
    try {
      setBusy(true);
      setError(null);
      const response = await axios.post(`${API_URL}/leave/attachments`, body, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange([...value, response.data]);
    } catch (err) {
      setError(errorText(err, isRTL ? 'فشل تحميل الملف' : 'Could not upload that file'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (attachment) => {
    try {
      setBusy(true);
      setError(null);
      await axios.delete(`${API_URL}/leave/attachments/${attachment.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(value.filter((a) => a.id !== attachment.id));
    } catch (err) {
      setError(errorText(err, isRTL ? 'فشل حذف الملف' : 'Could not remove that file'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2" data-testid="leave-attachment-uploader">
      <div className="flex items-center justify-between">
        <label className="text-sm font-medium flex items-center gap-1.5">
          <Paperclip className="h-3.5 w-3.5 text-slate-500" />
          {isRTL ? 'المرفقات' : 'Attachments'}
          {required && <span className="text-red-500">*</span>}
        </label>
        <span className="text-xs text-slate-500">
          {isRTL ? 'PDF أو JPG أو PNG · حتى 10 ميجابايت' : 'PDF, JPG or PNG · up to 10 MB'}
        </span>
      </div>

      {required && value.length === 0 && (
        <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
          {isRTL
            ? 'هذا النوع من الإجازات يتطلب مرفقاً (مثل تقرير طبي).'
            : 'This leave type requires a supporting document, such as a medical certificate.'}
        </p>
      )}

      {value.map((attachment) => (
        <div
          key={attachment.id}
          className="flex items-center gap-2 rounded border bg-slate-50 px-2 py-1.5 text-sm"
          data-testid={`leave-attachment-row-${attachment.id}`}
        >
          <FileText className="h-4 w-4 text-slate-500 flex-shrink-0" />
          <span className="truncate flex-1" title={attachment.file_name}>{attachment.file_name}</span>
          <span className="text-xs text-slate-500">{readableSize(attachment.file_size)}</span>
          <button
            type="button"
            onClick={() => remove(attachment)}
            disabled={busy}
            className="text-slate-400 hover:text-red-600 disabled:opacity-50"
            aria-label={isRTL ? 'إزالة' : 'Remove'}
            data-testid={`leave-attachment-remove-${attachment.id}`}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ))}

      {value.length < MAX_LEAVE_ATTACHMENTS && (
        <label className="block">
          <input
            type="file"
            accept={ACCEPT}
            onChange={handleFile}
            disabled={busy}
            className="hidden"
            data-testid="leave-attachment-input"
          />
          <span className="inline-flex cursor-pointer items-center gap-2 rounded border border-dashed border-slate-300 px-3 py-2 text-sm text-slate-600 transition-colors hover:border-slate-400 hover:bg-slate-50">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
            {isRTL ? 'إضافة ملف' : 'Add a file'}
          </span>
        </label>
      )}

      {error && <p className="text-sm text-red-600" data-testid="leave-attachment-error">{error}</p>}
    </div>
  );
};

/**
 * `showEmpty` makes the absence of a document explicit. The list used to
 * render nothing at all when it was empty OR when the call failed, which is
 * indistinguishable from "this feature does not exist" - part of why HR
 * reported that a submitted sick note could not be found anywhere.
 */
export const LeaveAttachmentList = ({
  token, requestId, isRTL, canRemove = false, onChanged, showEmpty = false,
}) => {
  const [attachments, setAttachments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    if (!requestId) return;
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/leave/requests/${requestId}/attachments`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setAttachments(response.data.attachments || []);
      setError(null);
    } catch (err) {
      setError(errorText(err, isRTL ? 'فشل تحميل المرفقات' : 'Could not load the attachments'));
    } finally {
      setLoading(false);
    }
  }, [requestId, token, isRTL]);

  useEffect(() => { load(); }, [load]);

  const download = async (attachment) => {
    try {
      setDownloading(attachment.id);
      setError(null);
      await downloadWithAuth(
        `${API_URL}/leave/attachments/${attachment.id}/download`,
        attachment.file_name,
        token,
      );
    } catch (err) {
      setError(isRTL ? 'فشل تنزيل الملف' : 'Could not download that file');
    } finally {
      setDownloading(null);
    }
  };

  const remove = async (attachment) => {
    try {
      await axios.delete(`${API_URL}/leave/attachments/${attachment.id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      await load();
      if (onChanged) onChanged();
    } catch (err) {
      setError(errorText(err, isRTL ? 'فشل حذف الملف' : 'Could not remove that file'));
    }
  };

  if (loading) {
    return (
      <p className="text-sm text-slate-500 flex items-center gap-2" data-testid="leave-attachment-loading">
        <Loader2 className="h-3.5 w-3.5 animate-spin" />
        {isRTL ? 'جاري التحميل...' : 'Loading attachments...'}
      </p>
    );
  }

  if (!error && attachments.length === 0 && !showEmpty) return null;

  return (
    <div className="space-y-2" data-testid="leave-attachment-list">
      <h4 className="font-medium flex items-center gap-1.5">
        <Paperclip className="h-3.5 w-3.5 text-slate-500" />
        {isRTL ? 'المرفقات' : 'Supporting documents'}
        {attachments.length > 0 && (
          <span className="text-xs font-normal text-slate-500">({attachments.length})</span>
        )}
      </h4>

      {error && (
        <p className="text-sm text-red-600" data-testid="leave-attachment-list-error">{error}</p>
      )}

      {!error && attachments.length === 0 && (
        <p
          className="text-sm text-slate-500 bg-slate-50 border border-slate-200 rounded px-2 py-1.5"
          data-testid="leave-attachment-empty"
        >
          {isRTL
            ? 'لم يرفق الموظف أي مستند مع هذا الطلب.'
            : 'The employee attached no document to this request.'}
        </p>
      )}

      {attachments.map((attachment) => (
        <div
          key={attachment.id}
          className="flex items-center gap-2 rounded border bg-slate-50 px-2 py-1.5 text-sm"
          data-testid={`leave-attachment-item-${attachment.id}`}
        >
          <FileText className="h-4 w-4 text-slate-500 flex-shrink-0" />
          <span className="truncate flex-1" title={attachment.file_name}>{attachment.file_name}</span>
          <span className="text-xs text-slate-500">{readableSize(attachment.file_size)}</span>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setPreview(attachment)}
            title={isRTL ? 'معاينة' : 'View on screen'}
            data-testid={`leave-attachment-preview-${attachment.id}`}
          >
            <Eye className="h-4 w-4" />
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={() => download(attachment)}
            disabled={downloading === attachment.id}
            title={isRTL ? 'تنزيل' : 'Download'}
            data-testid={`leave-attachment-download-${attachment.id}`}
          >
            {downloading === attachment.id
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Download className="h-4 w-4" />}
          </Button>
          {canRemove && (
            <button
              type="button"
              onClick={() => remove(attachment)}
              className="text-slate-400 hover:text-red-600"
              aria-label={isRTL ? 'إزالة' : 'Remove'}
              data-testid={`leave-attachment-list-remove-${attachment.id}`}
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
      ))}

      <DocumentPreview
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        document={preview}
        downloadUrl={preview ? `${API_URL}/leave/attachments/${preview.id}/download` : null}
        token={token}
        isRTL={isRTL}
        documentType="direct"
      />
    </div>
  );
};
